<script setup lang="ts">
  import { computed, onMounted } from "vue";
  import { storeToRefs } from "pinia";
  import { Plus, Trash2 } from "lucide-vue-next";
  import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { Label } from "@/components/ui/label";
  import { Select } from "@/components/episto-ui/select";
  import { Tooltip } from "@/components/ui/tooltip";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import AnswersQuotas from "./AnswersQuotas.vue";
  import { useQuotasStore } from "@stores/survey/quotas";
  import type { Quota } from "@/types";

  const quotasStore = useQuotasStore();
  const { addQuota, removeQuota, setQuestion, fetchQuotas, quotaErrors } = quotasStore;
  const { currentQuotas, questionOptions, quotaTypeOptions, quotaUnitOptions } = storeToRefs(quotasStore);

  const toggles = computed(() => [
    { key: "quota_type" as const, label: "Quota type", options: quotaTypeOptions.value },
    { key: "unit" as const, label: "Quota unit", options: quotaUnitOptions.value },
  ]);

  const setToggle = (quota: Quota, key: "quota_type" | "unit", value: string) => {
    (quota[key] as string) = value;
  };

  onMounted(fetchQuotas);
</script>

<template>
  <Card class="p-6">
    <CardHeader class="pb-6">
      <CardTitle class="heading-page">Quotas</CardTitle>
    </CardHeader>
    <CardContent class="space-y-4">
      <div
        v-for="(quota, index) in currentQuotas"
        :key="quota.id ?? `new-${index}`"
        class="space-y-4 rounded-xl border p-5">
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0 flex-1 space-y-2">
            <span class="heading-label block">Quota {{ index + 1 }}</span>
            <Select
              class="w-1/2"
              :model-value="quota.question_id"
              :options="questionOptions"
              placeholder="Select a question"
              filterable
              @update:model-value="setQuestion(quota, $event)" />
          </div>
          <ConfirmDialog
            title="Delete Quota"
            description="Are you sure you want to delete this quota? This action cannot be undone."
            confirm-label="Delete"
            @confirm="removeQuota(quota)">
            <template #trigger>
              <Tooltip content="Delete quota">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  class="text-muted-foreground hover:text-destructive">
                  <Trash2 class="size-4" />
                </Button>
              </Tooltip>
            </template>
          </ConfirmDialog>
        </div>

        <div class="flex flex-wrap gap-2">
          <Badge
            v-for="error in quotaErrors(quota)"
            :key="error"
            variant="destructive">
            {{ error }}
          </Badge>
        </div>

        <div
          v-if="quota.question_id"
          class="flex flex-wrap gap-8">
          <div
            v-for="toggle in toggles"
            :key="toggle.key"
            class="flex flex-col gap-1.5">
            <Label>{{ toggle.label }}</Label>
            <div class="inline-flex w-fit gap-0.5 rounded-lg border p-0.5">
              <button
                v-for="option in toggle.options"
                :key="option.value"
                type="button"
                class="rounded-md px-3 py-1.5 text-13 transition-colors"
                :class="
                  quota[toggle.key] === option.value
                    ? 'bg-secondary font-semibold text-primary'
                    : 'text-muted-foreground hover:text-primary'
                "
                @click="setToggle(quota, toggle.key, String(option.value))">
                {{ option.label }}
              </button>
            </div>
          </div>
        </div>

        <AnswersQuotas
          v-if="quota.question_id"
          :index="index" />
      </div>

      <Button
        class="w-fit"
        variant="outline"
        @click="addQuota()">
        <Plus class="size-4" />
        Add quota
      </Button>
    </CardContent>
  </Card>
</template>
